import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link as MuiLink,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatDecimalString } from '../../shared/format/decimal';
import { withdrawalsRepositoryToken } from './repository';
import type {
  WithdrawalApprovalSummary,
  WithdrawalDetail,
  WithdrawalResolutionInput,
  WithdrawalResolutionPreview,
} from './types';

interface ResolutionForm {
  readonly resolution: '' | 'COMPLETE' | 'RELEASE';
  readonly reason: string;
  readonly evidenceReference: string;
  readonly partnerCreditId: string;
  readonly failureCode: string;
}

const initialForm: ResolutionForm = {
  resolution: '',
  reason: '',
  evidenceReference: '',
  partnerCreditId: '',
  failureCode: '',
};
const failureCodePattern = /^[A-Z][A-Z0-9_]{1,99}$/u;

export function WithdrawalResolutionDialog({
  canViewApproval,
  dataSource,
  onClose,
  open,
  withdrawal,
}: {
  readonly canViewApproval: boolean;
  readonly dataSource: DataSource;
  readonly onClose: (completed: boolean) => void;
  readonly open: boolean;
  readonly withdrawal: WithdrawalDetail;
}) {
  const repository = useRepository(withdrawalsRepositoryToken);
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [preview, setPreview] = useState<WithdrawalResolutionPreview | null>(null);
  const [approval, setApproval] = useState<WithdrawalApprovalSummary | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'submit' | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const previewingRef = useRef(false);
  const submittingRef = useRef(false);
  const valid = isValid(form);

  const update = (field: keyof ResolutionForm, value: string) => {
    setForm((current) => {
      if (field === 'resolution') {
        return {
          ...current,
          resolution: value as ResolutionForm['resolution'],
          partnerCreditId: '',
          failureCode: '',
        };
      }
      return { ...current, [field]: value };
    });
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setError(null);
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const reset = () => {
    setForm(initialForm);
    setPreview(null);
    setApproval(null);
    setConfirmed(false);
    setBusy(null);
    setError(null);
    previewingRef.current = false;
    submittingRef.current = false;
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const close = () => {
    if (busy !== null) return;
    const completed = approval !== null;
    reset();
    onClose(completed);
  };
  const requestPreview = async () => {
    if (!valid || previewingRef.current || busy !== null) return;
    previewingRef.current = true;
    setBusy('preview');
    setError(null);
    try {
      setPreview(await repository.previewResolution(withdrawal.id, normalize(form)));
      setConfirmed(false);
    } catch (cause) {
      setError(safeError(cause, t('withdrawals.resolution.errors.preview')));
    } finally {
      previewingRef.current = false;
      setBusy(null);
    }
  };
  const submit = async () => {
    if (preview === null || !confirmed || submittingRef.current || busy !== null) return;
    submittingRef.current = true;
    setBusy('submit');
    setError(null);
    try {
      const result = await repository.createResolution(
        withdrawal.id,
        { ...normalize(form), previewHash: preview.previewHash },
        idempotencyKeyRef.current,
      );
      setApproval(result.data);
      idempotencyKeyRef.current = crypto.randomUUID();
    } catch (cause) {
      setError(safeError(cause, t('withdrawals.resolution.errors.submit')));
      submittingRef.current = false;
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      aria-describedby="withdrawal-resolution-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('withdrawals.resolution.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <DialogContentText id="withdrawal-resolution-description">
            {t('withdrawals.resolution.description', {
              withdrawal: withdrawal.partnerWithdrawalId,
            })}
          </DialogContentText>
          {approval === null ? (
            <>
              <FormControl disabled={busy !== null} required>
                <FormLabel>{t('withdrawals.resolution.resolution')}</FormLabel>
                <RadioGroup
                  onChange={(event) => update('resolution', event.target.value)}
                  row
                  value={form.resolution}
                >
                  <FormControlLabel control={<Radio />} label="COMPLETE" value="COMPLETE" />
                  <FormControlLabel control={<Radio />} label="RELEASE" value="RELEASE" />
                </RadioGroup>
              </FormControl>
              {form.resolution === '' ? null : (
                <Alert severity="warning">
                  {form.resolution === 'COMPLETE'
                    ? t('withdrawals.resolution.completeProof')
                    : t('withdrawals.resolution.releaseProof')}
                </Alert>
              )}
              {form.resolution === 'COMPLETE' ? (
                <TextField
                  disabled={busy !== null}
                  label={t('withdrawals.resolution.partnerCreditId')}
                  onChange={(event) => update('partnerCreditId', event.target.value)}
                  required
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  value={form.partnerCreditId}
                />
              ) : null}
              {form.resolution === 'RELEASE' ? (
                <TextField
                  disabled={busy !== null}
                  error={form.failureCode.length > 0 && !failureCodePattern.test(form.failureCode)}
                  label={t('withdrawals.resolution.failureCode')}
                  onChange={(event) => update('failureCode', event.target.value.toUpperCase())}
                  required
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  value={form.failureCode}
                />
              ) : null}
              <TextField
                disabled={busy !== null}
                label={t('withdrawals.resolution.evidenceReference')}
                onChange={(event) => update('evidenceReference', event.target.value)}
                required
                slotProps={{ htmlInput: { maxLength: 1000 } }}
                value={form.evidenceReference}
              />
              <TextField
                disabled={busy !== null}
                label={t('withdrawals.resolution.reason')}
                minRows={2}
                multiline
                onChange={(event) => update('reason', event.target.value)}
                slotProps={{ htmlInput: { maxLength: 2000, required: true } }}
                value={form.reason}
              />
              {preview === null ? null : (
                <Paper component="section" sx={{ p: 2 }} variant="outlined">
                  <Typography component="h3" sx={{ mb: 1 }} variant="h6">
                    {t('withdrawals.resolution.impact')}
                  </Typography>
                  <MetadataList
                    items={[
                      { label: t('withdrawals.fields.internalId'), value: preview.withdrawalId },
                      {
                        label: t('withdrawals.resolution.amount'),
                        value: exactMoney(preview.amount, preview.currencyCode),
                      },
                      { label: t('withdrawals.fields.status'), value: withdrawal.status },
                      {
                        label: t('withdrawals.resolution.proposedState'),
                        value: preview.resolution === 'COMPLETE' ? 'COMPLETED' : 'FAILED_RELEASED',
                      },
                    ]}
                  />
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('withdrawals.resolution.approvalRequired')}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmed}
                        onChange={(event) => setConfirmed(event.target.checked)}
                      />
                    }
                    label={t('withdrawals.resolution.confirmImpact')}
                  />
                </Paper>
              )}
            </>
          ) : (
            <Alert severity="success">
              <Stack spacing={1}>
                <span>
                  {t('withdrawals.resolution.approvalCreated', { status: approval.status })}
                </span>
                <span>{approval.id}</span>
                {canViewApproval ? (
                  <MuiLink
                    component={Link}
                    to={`${paths.approvals}/${encodeURIComponent(approval.id)}`}
                  >
                    {t('withdrawals.resolution.viewApproval')}
                  </MuiLink>
                ) : null}
              </Stack>
            </Alert>
          )}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy !== null} onClick={close}>
          {t('actions.close')}
        </Button>
        {approval !== null ? null : preview === null ? (
          <Button
            disabled={!valid || busy !== null}
            loading={busy === 'preview'}
            onClick={() => void requestPreview()}
          >
            {t('withdrawals.resolution.preview')}
          </Button>
        ) : (
          <Button
            disabled={!confirmed || busy !== null}
            loading={busy === 'submit'}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('withdrawals.resolution.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function isValid(form: ResolutionForm) {
  const common =
    form.reason.trim().length > 0 &&
    form.reason.trim().length <= 2000 &&
    form.evidenceReference.trim().length > 0 &&
    form.evidenceReference.trim().length <= 1000;
  if (!common) return false;
  if (form.resolution === 'COMPLETE')
    return form.partnerCreditId.trim().length > 0 && form.partnerCreditId.trim().length <= 200;
  if (form.resolution === 'RELEASE') return failureCodePattern.test(form.failureCode.trim());
  return false;
}

function normalize(form: ResolutionForm): WithdrawalResolutionInput {
  const common = {
    reason: form.reason.trim(),
    evidenceReference: form.evidenceReference.trim(),
  };
  if (form.resolution === 'COMPLETE') {
    return { resolution: 'COMPLETE', ...common, partnerCreditId: form.partnerCreditId.trim() };
  }
  if (form.resolution === 'RELEASE') {
    return { resolution: 'RELEASE', ...common, failureCode: form.failureCode.trim() };
  }
  throw new Error('A resolution is required');
}

function exactMoney(amount: string, currencyCode: string) {
  return `${currencyCode.trim().toUpperCase()}\u00a0${formatDecimalString(amount)}`;
}

function safeError(cause: unknown, fallback: string): AppError {
  const error = isAppError(cause) ? cause : null;
  return {
    status: error?.status ?? null,
    code: error?.code ?? 'WITHDRAWAL_RESOLUTION_FAILED',
    message: fallback,
    requestId: error?.requestId ?? null,
    fieldErrors: {},
    retryAfterSeconds: error?.retryAfterSeconds ?? null,
  };
}

function isAppError(value: unknown): value is AppError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<AppError>).code === 'string' &&
    'requestId' in value
  );
}
